import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from "@/utils";
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Clock, MapPin, User, Calendar, AlertCircle, UserPlus, Phone, Mail } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { deliverMemberNotification } from '@/lib/notificationDelivery';
import { getMemberByUser } from '@/lib/memberLookup';

export default function Grupos() {
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [filter, setFilter] = useState('all');
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: currentUser, isLoading: loadingUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me()
  });

  const { data: memberData, isLoading: loadingMember } = useQuery({
    queryKey: ['activeMember', currentUser?.id, currentUser?.email],
    queryFn: async () => {
      const member = await getMemberByUser(currentUser);
      return member?.status === 'Ativo' ? member : null;
    },
    enabled: !!currentUser
  });
  const activeMember = memberData || null;

  const { data: groups = [], isLoading } = useQuery({
    queryKey: ['groups'],
    queryFn: () => base44.entities.Group.filter({ is_active: true }, 'name', 50),
    enabled: !!activeMember
  });

  const { data: allMembers = [] } = useQuery({
    queryKey: ['allMembers'],
    queryFn: () => base44.entities.Member.filter({ status: 'Ativo' }, 'full_name', 200),
    enabled: !!activeMember
  });

  const requestJoinMutation = useMutation({
    mutationFn: async ({ groupId, group }) => {
      const pendingRequests = group.pending_requests || [];
      const alreadyRequested = pendingRequests.some(r => r.member_id === activeMember.id);
      
      if (alreadyRequested) {
        throw new Error('Você já solicitou entrada neste grupo');
      }

      const updatedRequests = [
        ...pendingRequests,
        {
          member_id: activeMember.id,
          member_name: activeMember.full_name,
          requested_at: new Date().toISOString()
        }
      ];

      await base44.entities.Group.update(groupId, {
        pending_requests: updatedRequests
      });

      // Notificar líderes do grupo
      const leaders = group.leaders || [];
      for (const leader of leaders) {
        const leaderMember = allMembers.find(m => m.id === leader.leader_id);
      if (leaderMember) {
        // Criar notificação no app
        await deliverMemberNotification({
          recipients: [{ member_id: leaderMember.id, member_email: leaderMember.email }],
          title: 'Nova solicitação de entrada',
          message: `${activeMember.full_name} solicitou entrada no grupo ${group.name}`,
          type: 'general',
          referenceId: groupId,
        });

        // Enviar email
        try {
          await base44.integrations.Core.SendEmail({
            to: leaderMember.email,
            subject: `Nova solicitação de entrada - ${group.name}`,
            body: `
              <h2>Nova Solicitação de Entrada no Grupo</h2>
              <p><strong>${activeMember.full_name}</strong> solicitou entrada no grupo <strong>${group.name}</strong>.</p>
              <p>Acesse o sistema para aprovar ou rejeitar a solicitação.</p>
            `
          });
        } catch (error) {
          console.error('Erro ao enviar email:', error);
        }

        // Enviar WhatsApp
        if (leaderMember.phone) {
          try {
            await base44.functions.invoke('sendGroupNotificationWhatsApp', {
              recipientPhone: leaderMember.phone,
              message: `🔔 *Nova solicitação de entrada*\n\n*${activeMember.full_name}* solicitou entrada no grupo *${group.name}*.\n\nAcesse o sistema para aprovar ou rejeitar.`
            });
          } catch (error) {
            console.error('Erro ao enviar WhatsApp:', error);
          }
        }
      }
      }

      return { groupId, group };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast.success('Solicitação enviada com sucesso!');
      setSelectedGroup(null);
    },
    onError: (error) => {
      toast.error(error.message || 'Erro ao solicitar entrada');
    }
  });

  const isMemberOfGroup = (group) => {
    return group.member_ids?.includes(activeMember?.id);
  };

  const hasPendingRequest = (group) => {
    return group.pending_requests?.some(r => r.member_id === activeMember?.id);
  };

  const getGroupMembers = (group) => {
    return allMembers.filter(m => group.member_ids?.includes(m.id));
  };

  const getLeaderInfo = (group) => {
    if (!group.leader_id) return null;
    return allMembers.find(m => m.id === group.leader_id);
  };

  const handleRequestJoin = (group) => {
    if (isMemberOfGroup(group)) {
      toast.info('Você já é membro deste grupo');
      return;
    }
    requestJoinMutation.mutate({ groupId: group.id, group });
  };

  const groupTypes = ['all', ...new Set(groups.map(g => g.type).filter(Boolean))];

  const filteredGroups = filter === 'all'
    ? groups
    : groups.filter(g => g.type === filter);

  const typeColors = {
    'Célula': 'bg-emerald-100 text-emerald-700',
    'Estudo Bíblico': 'bg-blue-100 text-blue-700',
    'Jovens': 'bg-purple-100 text-purple-700',
    'Oração': 'bg-rose-100 text-rose-700',
    'Visita': 'bg-amber-100 text-amber-700',
    'Ministério de Crianças': 'bg-pink-100 text-pink-700',
    'Reunião': 'bg-indigo-100 text-indigo-700',
    'Líderes': 'bg-slate-100 text-slate-700',
    'Louvor': 'bg-cyan-100 text-cyan-700',
    'Outros': 'bg-gray-100 text-gray-700',
  };

  if (loadingUser || loadingMember || (activeMember && isLoading) || (!currentUser && loadingUser)) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <LoadingSpinner />
      </div>
    );
  }

  if (!activeMember) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24">
        <div className="bg-gradient-to-br from-sky-500 to-blue-600 text-white pt-8 pb-6 px-4">
          <div className="max-w-lg mx-auto">
            <PageHeader title="Ministérios" subtitle="Grupos e células" showBack={true} />
          </div>
        </div>
        <div className="max-w-lg mx-auto px-4 py-12">
          <div className="bg-white rounded-xl p-8 shadow-sm text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-amber-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Acesso Restrito</h3>
            <p className="text-gray-600 mb-4">Apenas membros ativos podem acessar os ministérios.</p>
            <Button onClick={() => navigate(createPageUrl('CadastroMembro'))}>
              Completar Cadastro
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-gradient-to-br from-sky-500 to-blue-600 text-white pt-8 pb-6 px-4">
        <div className="max-w-lg mx-auto">
          <PageHeader title="Ministérios" subtitle="Grupos e células" showBack={true} />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Type Filter */}
        {groupTypes.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-4 scrollbar-hide mb-4">
            {groupTypes.map(type => (
              <button
                key={type}
                onClick={() => setFilter(type)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  filter === type
                    ? 'bg-sky-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
              >
                {type === 'all' ? 'Todos' : type}
              </button>
            ))}
          </div>
        )}

        {filteredGroups.length === 0 ? (
          <EmptyState
            icon={Users}
            title="Nenhum ministério disponível"
            description="Em breve teremos ministérios para você participar."
          />
        ) : (
          <div className="space-y-4">
            {filteredGroups.map((group, index) => (
              <motion.div
                key={group.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-2xl shadow-sm p-4 cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => setSelectedGroup(group)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Users className="w-6 h-6 text-sky-600" />
                    </div>
                    <div>
                     <h3 className="font-semibold text-gray-900">{group.name}</h3>
                     <div className="flex items-center gap-2 mt-1 flex-wrap">
                       <Badge className={`${typeColors[group.type] || typeColors['Outros']}`}>
                         {group.type}
                       </Badge>
                       {group.member_ids && group.member_ids.length > 0 && (
                         <Badge variant="outline" className="text-xs">
                           {group.member_ids.length} {group.member_ids.length === 1 ? 'membro' : 'membros'}
                         </Badge>
                       )}
                     </div>
                     {group.leaders && group.leaders.length > 0 && (
                       <div className="flex flex-wrap gap-1 mt-2">
                         {group.leaders.map((leader, idx) => (
                           <span key={idx} className="inline-flex items-center gap-1 text-sm text-gray-500 bg-gray-50 px-2 py-0.5 rounded">
                             <User className="w-3 h-3" />
                             {leader.leader_name}
                           </span>
                         ))}
                       </div>
                     )}
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 mt-3 pt-3 border-t">
                  <Button 
                    variant="outline" 
                    className="w-full text-sm bg-sky-600 text-white border-sky-600 hover:bg-sky-700 sm:bg-transparent sm:text-foreground sm:border-input sm:hover:bg-accent sm:hover:text-accent-foreground"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedGroup(group);
                    }}
                  >
                    Detalhes
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Group Detail Modal */}
      <AnimatePresence>
        {selectedGroup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-end justify-center z-50 p-4"
            onClick={() => setSelectedGroup(null)}
          >
            <motion.div
              initial={{ y: 100 }}
              animate={{ y: 0 }}
              exit={{ y: 100 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-t-3xl w-full max-w-lg p-6 max-h-[80vh] overflow-auto"
            >
              <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-6" />

              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-sky-100 rounded-2xl flex items-center justify-center">
                  <Users className="w-8 h-8 text-sky-600" />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-gray-900">{selectedGroup.name}</h2>
                  <Badge className={`mt-1 ${typeColors[selectedGroup.type] || typeColors['Outros']}`}>
                    {selectedGroup.type}
                  </Badge>
                </div>
              </div>

              {selectedGroup.description && (
                <p className="text-gray-600 mt-4">{selectedGroup.description}</p>
              )}

              <div className="space-y-3 mt-6">
                {selectedGroup.leaders && selectedGroup.leaders.length > 0 && (
                  <div className="flex items-start gap-3 text-gray-600">
                    <User className="w-5 h-5 text-sky-500 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">Líderes</p>
                      <div className="space-y-3 mt-1">
                        {selectedGroup.leaders.map((leader, idx) => {
                          const leaderInfo = allMembers.find(m => m.id === leader.leader_id);
                          return (
                            <div key={idx}>
                              <p className="font-medium">{leader.leader_name}</p>
                              {leaderInfo && (
                                <div className="mt-1 space-y-1">
                                  {leaderInfo.phone && (
                                    <a href={`tel:${leaderInfo.phone}`} className="flex items-center gap-2 text-sm text-sky-600 hover:text-sky-700">
                                      <Phone className="w-4 h-4" />
                                      {leaderInfo.phone}
                                    </a>
                                  )}
                                  {leaderInfo.email && (
                                    <a href={`mailto:${leaderInfo.email}`} className="flex items-center gap-2 text-sm text-sky-600 hover:text-sky-700">
                                      <Mail className="w-4 h-4" />
                                      {leaderInfo.email}
                                    </a>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {selectedGroup.member_ids && selectedGroup.member_ids.length > 0 && (
                  <div className="flex items-center gap-3 text-gray-600">
                    <Users className="w-5 h-5 text-sky-500" />
                    <div>
                      <p className="text-sm text-gray-500">Membros</p>
                      <p className="font-medium">{selectedGroup.member_ids.length} {selectedGroup.member_ids.length === 1 ? 'membro' : 'membros'}</p>
                    </div>
                  </div>
                )}

                {selectedGroup.meeting_day && (
                  <div className="flex items-center gap-3 text-gray-600">
                    <Calendar className="w-5 h-5 text-sky-500" />
                    <div>
                      <p className="text-sm text-gray-500">Dia de Reunião</p>
                      <p className="font-medium">{selectedGroup.meeting_day}</p>
                    </div>
                  </div>
                )}

                {selectedGroup.meeting_time && (
                  <div className="flex items-center gap-3 text-gray-600">
                    <Clock className="w-5 h-5 text-sky-500" />
                    <div>
                      <p className="text-sm text-gray-500">Horário</p>
                      <p className="font-medium">{selectedGroup.meeting_time}</p>
                    </div>
                  </div>
                )}

                {selectedGroup.meeting_location && (
                  <div className="flex items-center gap-3 text-gray-600">
                    <MapPin className="w-5 h-5 text-sky-500" />
                    <div>
                      <p className="text-sm text-gray-500">Local</p>
                      <p className="font-medium">{selectedGroup.meeting_location}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                {isMemberOfGroup(selectedGroup) ? (
                  <Button
                    className="w-full"
                    onClick={() => setSelectedGroup(null)}
                  >
                    Fechar
                  </Button>
                ) : hasPendingRequest(selectedGroup) ? (
                  <div className="w-full flex items-center justify-center gap-2 py-3 bg-amber-50 text-amber-700 rounded-lg">
                    <Clock className="w-5 h-5" />
                    <span className="font-medium">Solicitação pendente</span>
                  </div>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      className="w-full sm:flex-1"
                      onClick={() => setSelectedGroup(null)}
                    >
                      Fechar
                    </Button>
                    <Button
                      className="w-full sm:flex-1 bg-sky-600 hover:bg-sky-700"
                      onClick={() => handleRequestJoin(selectedGroup)}
                      disabled={requestJoinMutation.isPending}
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Solicitar Entrada
                    </Button>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
